import {
  getScreeningOfferReviews,
  getEvaluationOfferReviews,
  upsertScreeningOfferReview,
  upsertEvaluationOfferReview,
  deleteScreeningOfferReview,
  deleteEvaluationOfferReview,
  getContractScreeningOffers,
  getContractEvaluationOffers,
  createScreeningReviewComment,
  createEvaluationReviewComment,
  createScreeningReviewAnswer,
  createEvaluationReviewAnswer,
  updateScreeningReviewComment,
  updateEvaluationReviewComment,
  deleteScreeningReviewComment,
  deleteEvaluationReviewComment,
} from '~src/services/api/offers';
import type { ReviewServiceFunctions, ReviewPhase } from '../types';

export const useOfferReviewService = (
  projectId: number,
  tenderId: number,
  contractId: number,
  phase: ReviewPhase
): ReviewServiceFunctions => {
  const isScreening = phase === 'screening';

  return {
    getReviews: async ({ entityId, skip, take, sort }) => {
      const fn = isScreening
        ? getScreeningOfferReviews
        : getEvaluationOfferReviews;
      return fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
        skip,
        take,
        sort,
      });
    },

    getDashboard: async ({ entityId }) => {
      const fn = isScreening
        ? getContractScreeningOffers
        : getContractEvaluationOffers;

      const offers = await fn({
        projectId,
        tenderId,
        contractId,
        includeDashboard: true,
        skip: 0,
        take: 1000,
      });

      const offer = offers.find((o: any) => o.id === entityId);

      if (isScreening) {
        return offer?.screening?.screening_binary_review_dashboard || null;
      } else {
        return offer?.evaluation?.evaluation_star_review_dashboard || null;
      }
    },

    upsertReview: async ({ entityId, body }) => {
      const fn = isScreening
        ? upsertScreeningOfferReview
        : upsertEvaluationOfferReview;
      return fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
        body,
      });
    },

    deleteReview: async ({ entityId }) => {
      const fn = isScreening
        ? deleteScreeningOfferReview
        : deleteEvaluationOfferReview;
      return fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
      });
    },

    createComment: async ({ entityId, reviewId, body }) => {
      const fn = isScreening
        ? createScreeningReviewComment
        : createEvaluationReviewComment;
      await fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
        reviewId,
        body,
      });
    },

    createAnswer: async ({ entityId, reviewId, commentId, body }) => {
      const fn = isScreening
        ? createScreeningReviewAnswer
        : createEvaluationReviewAnswer;
      await fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
        reviewId,
        commentId,
        body,
      });
    },

    updateComment: async ({ entityId, reviewId, commentId, body }) => {
      const fn = isScreening
        ? updateScreeningReviewComment
        : updateEvaluationReviewComment;
      await fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
        reviewId,
        commentId,
        body,
      });
    },

    deleteComment: async ({ entityId, reviewId, commentId }) => {
      const fn = isScreening
        ? deleteScreeningReviewComment
        : deleteEvaluationReviewComment;
      await fn({
        projectId,
        tenderId,
        contractId,
        offerId: entityId,
        reviewId,
        commentId,
      });
    },
  };
};
